import { DocumentProcessorResponse, CardGeneratorResponse, PDFGeneratorResponse, ExportOptions, EvaluationResponse } from '../types/ApiResponses';
import { GenerationRequest } from '../types/Topic';
import { Card } from '../types/Card';
import { CurriculumTopic, PreGeneratedCard } from '../types/Curriculum';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.brainbreakcards.com';

// Token getter — set by AuthContext after sign-in
let getIdToken: (() => string | null) = () => null;
let onAuthError: (() => void) | null = null;
export function setTokenProvider(fn: () => string | null) {
  getIdToken = fn;
}
export function setAuthErrorHandler(fn: () => void) {
  onAuthError = fn;
}

class ApiClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getIdToken();

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    });

    if (response.status === 401) {
      if (onAuthError) onAuthError();
      throw new Error('Session expired. Please sign in again.');
    }

    if (response.status === 429) {
      const data = await response.json().catch(() => ({}));
      const quota = data.quota;
      throw Object.assign(
        new Error(quota
          ? `You've used all ${quota.generationsLimit} card generations for this month. Resets on ${new Date(quota.resetsAt * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.`
          : 'Monthly generation limit reached.'
        ),
        { code: 'QUOTA_EXCEEDED', quota }
      );
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async processDocument(fileContent: string, fileName: string, fileType: string): Promise<DocumentProcessorResponse> {
    return this.request<DocumentProcessorResponse>('/api/documents/process', {
      method: 'POST',
      body: JSON.stringify({ fileContent, fileName, fileType }),
    });
  }

  async generateCards(request: GenerationRequest): Promise<CardGeneratorResponse> {
    return this.request<CardGeneratorResponse>('/api/cards/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async generateFromCurriculum(topics: CurriculumTopic[], preSelectedCards: PreGeneratedCard[]): Promise<CardGeneratorResponse> {
    return this.request<CardGeneratorResponse>('/api/cards/generate', {
      method: 'POST',
      body: JSON.stringify({
        topics: topics.map(t => ({
          name: t.topicName,
          subject: t.subject,
          yearGroup: t.yearGroup,
          keyConcepts: t.keyConcepts,
          confidence: 1.0,
        })),
        preSelectedCards: preSelectedCards.map(c => c.cardId),
        options: { regenerateUnpinned: false },
      }),
    });
  }

  async evaluateModels(topics: import('../types/Topic').Topic[]): Promise<EvaluationResponse> {
    return this.request<EvaluationResponse>('/api/cards/evaluate', {
      method: 'POST',
      body: JSON.stringify({ topics }),
    });
  }

  async exportToPDF(cards: Card[], options: ExportOptions = {}): Promise<PDFGeneratorResponse> {
    return this.request<PDFGeneratorResponse>('/api/export/pdf', {
      method: 'POST',
      body: JSON.stringify({ cards, options }),
    });
  }

  // Admin endpoints
  async adminListCards(query: string): Promise<{ topics: any[] }> {
    return this.request(`/api/admin/curriculum/cards?${query}`);
  }

  async adminUpdateCard(cardId: string, body: Record<string, string>): Promise<any> {
    return this.request(`/api/admin/curriculum/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async adminDeleteCard(cardId: string, topicId: string): Promise<any> {
    return this.request(`/api/admin/curriculum/cards/${cardId}?topicId=${topicId}`, {
      method: 'DELETE',
    });
  }

  async flagCard(cardId: string, topicId: string): Promise<any> {
    return this.request(`/api/curriculum/cards/${cardId}/flag?topicId=${topicId}`, {
      method: 'POST',
    });
  }

  async adminListFlagged(): Promise<{ flaggedCards: any[] }> {
    return this.request('/api/admin/curriculum/flagged');
  }

  async adminApproveCard(cardId: string, topicId: string, action: 'approve' | 'reject'): Promise<any> {
    return this.request(`/api/admin/curriculum/cards/${cardId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ topicId, action }),
    });
  }
}

export const apiClient = new ApiClient();
